import { Fragment } from "react";
import { eur, dt } from "@/lib/api";
import { Kpi } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2 } from "lucide-react";
import { DateField } from "@/components/DateField";

const TABS = [["cassa", "Cassa e banca"], ["giornale", "Libro giornale"], ["mastrino", "Schede contabili"], ["verifica", "Bilancio di verifica"],
  ["bilancio", "Conto economico e stato patrimoniale"], ["registri", "Registri IVA"], ["iva", "Liquidazione IVA"], ["reddito", "Prospetto del reddito"], ["esercizi", "Esercizi"], ["piano", "Piano dei conti"]];
const PER_REGIME = { ordinaria: ["cassa", "giornale", "mastrino", "verifica", "bilancio", "registri", "iva", "esercizi", "piano"],
  semplificata: ["cassa", "registri", "iva", "reddito"], professionisti: ["cassa", "registri", "iva", "reddito"], forfettaria: ["cassa", "registri", "reddito"] };
export const REGIME_LABEL = { ordinaria: "Contabilità ordinaria in partita doppia", semplificata: "Contabilità semplificata", professionisti: "Contabilità professionisti", forfettaria: "Regime forfettario" };

export const PnTabs = ({ tab, setTab, regime = "ordinaria" }) => (
  <div className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-slate-100 p-1" data-testid="pn-tabs">
    {TABS.filter(([k]) => (PER_REGIME[regime] || PER_REGIME.ordinaria).includes(k)).map(([k, l]) => (
      <button key={k} type="button" data-testid={`pn-tab-${k}`} onClick={() => setTab(k)}
        className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors ${tab === k ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-white"}`}>{k === "registri" && regime === "forfettaria" ? "Registro fatture" : l}</button>
    ))}
  </div>
);

export const PnFiltri = ({ f, set, conti }) => (
  <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="pn-filters">
    <label className="text-xs font-medium text-slate-600">Dal<DateField clearable className="mt-1 w-40" testid="pn-from" value={f.dal} onChange={(v) => set({ ...f, dal: v })} /></label>
    <label className="text-xs font-medium text-slate-600">Al<DateField clearable className="mt-1 w-40" testid="pn-to" value={f.al} onChange={(v) => set({ ...f, al: v })} /></label>
    <label className="text-xs font-medium text-slate-600">Conto
      <Select value={f.conto_id || "all"} onValueChange={(v) => set({ ...f, conto_id: v === "all" ? "" : v })}>
        <SelectTrigger className="mt-1 w-48" data-testid="pn-account-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Tutti i conti</SelectItem>{conti.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
      </Select>
    </label>
  </div>
);

export const PnTabella = ({ rep, onEdit, onDelete }) => (
  <>
    <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Kpi label="Saldo iniziale" value={eur(rep.saldo_iniziale)} testid="pn-kpi-start" />
      <Kpi label="Entrate" value={eur(rep.entrate)} tone="emerald" testid="pn-kpi-in" />
      <Kpi label="Uscite" value={eur(rep.uscite)} tone="rose" testid="pn-kpi-out" />
      <Kpi label="Saldo finale" value={eur(rep.saldo_finale)} testid="pn-kpi-end" />
    </div>
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <Table data-testid="pn-table">
        <TableHeader><TableRow className="bg-slate-50"><TableHead>Data</TableHead><TableHead>Descrizione</TableHead><TableHead>Causale</TableHead><TableHead>Conto</TableHead>
          <TableHead className="text-right">Entrate</TableHead><TableHead className="text-right">Uscite</TableHead><TableHead className="text-right">Saldo</TableHead>{onEdit && <TableHead />}</TableRow></TableHeader>
        <TableBody>
          <TableRow className="bg-sky-50/50"><TableCell colSpan={6} className="text-sm font-semibold">Saldo iniziale al {dt(rep.dal)}</TableCell><TableCell className="text-right font-mono font-semibold">{eur(rep.saldo_iniziale)}</TableCell>{onEdit && <TableCell />}</TableRow>
          {rep.items.map((r) => (
            <Fragment key={r.id}>
              <TableRow data-testid={`pn-row-${r.id}`}>
                <TableCell className="whitespace-nowrap text-sm">{dt(r.data)}</TableCell>
                <TableCell className="text-sm">{r.descrizione}{r.auto && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">automatico</span>}</TableCell>
                <TableCell className="text-sm text-slate-600">{r.causale}</TableCell>
                <TableCell className="text-sm">{r.conto}</TableCell>
                <TableCell className="text-right font-mono text-sm text-emerald-700">{r.entrata ? eur(r.entrata) : ""}</TableCell>
                <TableCell className="text-right font-mono text-sm text-rose-700">{r.uscita ? eur(r.uscita) : ""}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{eur(r.saldo)}</TableCell>
                {onEdit && <TableCell className="whitespace-nowrap text-right">{!r.auto && <>
                  <Button variant="ghost" size="icon" data-testid={`pn-edit-${r.id}`} onClick={() => onEdit(r)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`pn-delete-${r.id}`} onClick={() => onDelete(r)}><Trash2 className="h-4 w-4 text-rose-600" /></Button></>}</TableCell>}
              </TableRow>
            </Fragment>
          ))}
        </TableBody>
      </Table>
      {rep.items.length === 0 && <p className="p-4 text-sm text-slate-500" data-testid="pn-empty">Nessun movimento nel periodo.</p>}
    </div>
  </>
);
